import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithCredentials } from '@/features/auth';
import { prefetchProductBindings } from '@/features/auth/api/product-binding';
import { ScopeContextStorage } from '@/shared/lib/scope-storage.ts';
import { secureSessionStorage } from '@/shared/lib/storage.ts';
import { DebugLogger } from '@/shared/lib/debug.ts';
import { acceptLanguage } from '@/shared/lib/accept-language';
import { parseBackendErrors } from '@/shared/lib/backend-errors';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { FormField } from '@/shared/ui/form-field';
import { Label } from '@/shared/ui/label';
import { LanguageToggle } from '@/shared/ui/language-toggle';
import { Logo } from '@/shared/ui/logo';
import { Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/shared/hooks/use-toast';
import { useLanguage } from '@/shared/hooks/use-language';

const LOGIN_FIELD_MAP: Record<string, string> = {
  'User-Id': 'email',
  'user_id': 'email',
  'public_key': 'password',
};

function Login() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  useEffect(() => {
    fetch('/api/welcome', { headers: { 'Accept-Language': acceptLanguage() } }).catch(() => {});

    let cancelled = false;
    (async () => {
      const sessionId = ScopeContextStorage.getCurrentSessionId();
      if (!sessionId) return;
      const key = await secureSessionStorage.getSessionKey(sessionId);
      if (!key || cancelled) return;
      const scope = ScopeContextStorage.getScopeContext(sessionId);
      if (scope && !ScopeContextStorage.needsAccountHolderSelection(scope)) {
        if (ScopeContextStorage.needsAccountSelection(scope)) {
          navigate('/account-selection', {
            state: { sessionId, scopeContext: scope },
            replace: true,
          });
        } else {
          prefetchProductBindings(sessionId, scope);
          navigate('/console', {
            state: { sessionId, scopeContext: scope },
            replace: true,
          });
        }
      } else {
        navigate('/account-holder-selection', {
          state: { sessionId },
          replace: true,
        });
      }
    })().catch((err) => {
      DebugLogger.error('Auto-resume failed', { err });
    });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    const localErrors: Record<string, string> = {};
    if (!email.trim()) {
      localErrors.email = t("login.emailRequired") || 'Email is required';
    }
    if (!password) {
      localErrors.password = t("login.passwordRequired") || 'Password is required';
    }
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setFieldErrors({});

    try {
      DebugLogger.debug('Starting login process', { email, keepSignedIn });
      const result = await signInWithCredentials(email, password, keepSignedIn);
      
      DebugLogger.debug('Login result received', { 
        success: result.success, 
        sessionId: result.sessionId,
        error: result.error 
      });
      
      if (result.success) {
        DebugLogger.debug('Login successful, checking for existing scope context', { 
          sessionId: result.sessionId 
        });
        
        toast({
          title: t("login.successTitle"),
          description: t("login.successDescription"),
        });
        
        // Check if we have a stored scope context for this session
        const storedContext = ScopeContextStorage.getScopeContext(result.sessionId!);
        
        if (storedContext && !ScopeContextStorage.needsAccountHolderSelection(storedContext)) {
          if (ScopeContextStorage.needsAccountSelection(storedContext)) {
            DebugLogger.debug('Account selection needed, navigating to account selection', { 
              sessionId: result.sessionId,
              storedContext 
            });
            navigate('/account-selection', { 
              state: { sessionId: result.sessionId, scopeContext: storedContext } 
            });
          } else {
            DebugLogger.debug('Scope complete, navigating to console', {
              sessionId: result.sessionId,
              storedContext
            });
            prefetchProductBindings(result.sessionId!, storedContext);
            navigate('/console', {
              state: { sessionId: result.sessionId, scopeContext: storedContext }
            });
          }
        } else {
          DebugLogger.debug('No stored scope context, navigating to account holder selection', { 
            sessionId: result.sessionId 
          });
          navigate('/account-holder-selection', { 
            state: { sessionId: result.sessionId } 
          });
        }
      } else {
        DebugLogger.error('Login failed', { error: result.error });
        const errorMessage = result.error || 'Authentication failed';
        const { fieldErrors: backendFieldErrors, generalError } = parseBackendErrors(
          result.validationError,
          LOGIN_FIELD_MAP,
        );
        if (Object.keys(backendFieldErrors).length > 0) {
          setFieldErrors(backendFieldErrors);
          setError(generalError);
        } else {
          setError(errorMessage);
        }
        toast({
          title: t("login.errorTitle"),
          description: errorMessage,
          variant: "destructive",
        });
      }
    } catch (error) {
      DebugLogger.error('Login process failed', { error });
      const errorMessage = 'An unexpected error occurred during sign-in';
      setError(errorMessage);
      toast({
        title: t("login.errorTitle"),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
      <div className="w-full max-w-md p-8">
        <div className="mb-8 text-center">
          <Logo className="justify-center mb-4" />
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            {t("login.welcome")}
          </h1>
          <p className="text-muted-foreground">
            {t("login.subtitle")}
          </p>
        </div>

        <div className="bg-card p-8 rounded-2xl border border-border card-hover">
          <form onSubmit={handleLogin} className="space-y-6" noValidate>
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <FormField
              id="email"
              type="email"
              label={t("login.email")}
              placeholder={t("login.emailPlaceholder")}
              value={email}
              onChange={(value) => {
                setEmail(value);
                clearFieldError('email');
              }}
              error={fieldErrors.email}
              disabled={isLoading}
              required
              autoComplete="email"
            />

            <FormField
              id="password"
              type={showPassword ? "text" : "password"}
              label={t("login.password")}
              placeholder="••••••••"
              value={password}
              onChange={(value) => {
                setPassword(value);
                clearFieldError('password');
              }}
              error={fieldErrors.password}
              disabled={isLoading}
              required
              autoComplete="current-password"
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="pr-3 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              }
            />

            <div className="flex items-center space-x-2">
              <Checkbox
                id="keep-signed-in"
                checked={keepSignedIn}
                onCheckedChange={(checked) => setKeepSignedIn(checked === true)}
                disabled={isLoading}
              />
              <Label
                htmlFor="keep-signed-in"
                className="text-sm text-muted-foreground cursor-pointer select-none"
              >
                {t("login.keepSignedIn")}
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground btn-arrow"
              disabled={isLoading}
            >
              {isLoading ? t("login.authenticating") : t("login.submit")}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {t("login.forgotPassword")}
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <LanguageToggle />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
